import { useMemo } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { TFunction, useTranslation } from "react-i18next";

import {
    ControlProgramOverviewResource,
    ControlProgramTagCategoryView,
    ControlProgramTagView,
} from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { TagList } from "components/tag/tagList";
import { TagsData } from "components/tag/types";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import {
    formatTagsAndCategoriesString,
    tagsTSTFilterFn,
    tagsTSTSortingFn,
} from "org/tags/tagsTSTUtils";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    ENTITY_PROGRAMS_LIST_COLUMN_IDS as ID,
    buildEntityProgramsListTableStateStorageKey,
    entityProgramsListColumnFiltersCodecMap,
    entityProgramsListTableDefaultValues,
} from "./consts";

interface ProgramTableRow {
    controlProgramId: number;
    name: string;
    controlCount: number;
    running: boolean;
    tagIds: string[];
}

type EntityProgramListColumnFilters = ZodCodecMapToType<
    typeof entityProgramsListColumnFiltersCodecMap
>;

type EntityProgramListColumnId = (typeof ID)[keyof typeof ID];

export function useEntityProgramListTable(
    programsWithEntities: ControlProgramOverviewResource[],
    tags: ControlProgramTagView[],
    tagCategories: ControlProgramTagCategoryView[],
    entityId: number
) {
    const { t } = useTranslation();
    const tagsData: TagsData = formatToTagsData(tags, tagCategories);
    const columns = buildColumns(t, tagsData);
    const tableData: ProgramTableRow[] = useMemo(() => {
        return programsWithEntities.map((program) => ({
            controlProgramId: program.controlProgram.id,
            name: program.controlProgram.name,
            controlCount: program.controlCount,
            running: program.controlProgram.running,
            tagIds: program.tagIds,
        }));
    }, [programsWithEntities]);

    const notificationLabel = t("Table_RemovedFiltersWarning");
    const tableId = buildEntityProgramsListTableStateStorageKey(entityId);
    const stateAndOnChanges = usePresetTableSearchParams(
        tableId,
        entityProgramsListTableDefaultValues,
        entityProgramsListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );

    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        initialState: {
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ProgramTableRow>(),
        getSortedRowModel: getSortedRowModel<ProgramTableRow>(),
        getFilteredRowModel: getFilteredRowModel<ProgramTableRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ProgramTableRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ProgramTableRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const getFilterComponentWrapper = (
        column: Column<ProgramTableRow>,
        target: FilterTarget
    ) => getFilterComponent(column, target, tagsData, t);

    return {
        table,
        getFilterComponent: getFilterComponentWrapper,
        canAddException: (column: Column<ProgramTableRow>) =>
            column.id === ID.TAGS,
        tableId,
    };
}

function buildColumns(t: TFunction, tagsData: TagsData) {
    const columnHelper = createColumnHelper<ProgramTableRow>();
    return [
        columnHelper.accessor((row) => row.name, {
            id: ID.NAME,
            header: () => t("ControlProgram_List_Header_Name"),
            cell: (info) => {
                const controlProgramId = info.row.original.controlProgramId;
                const url = buildControlProgramViewUrl(controlProgramId);
                const name = info.getValue();
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        {name}
                    </a>
                );
            },
            size: TST_COLUMN_SIZE.XL,
            enableColumnFilter: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode<ProgramTableRow, string[]>(
                (row, value) => tstArrIncludesSomeFilterFn(row, ID.NAME, value)
            ),
        }),
        columnHelper.accessor((row) => row.controlCount, {
            id: ID.CONTROL_COUNT,
            header: () => t("ControlProgram_List_Header_Controls"),
            cell: (info) => {
                const count = info.getValue();
                return <span>{count}</span>;
            },
            enableColumnFilter: false,
            sortingFn: "basic",
            size: TST_COLUMN_SIZE.XS,
        }),
        columnHelper.accessor((row) => String(row.running), {
            id: ID.STATUS,
            header: () => t("ControlProgram_List_Header_Status"),
            cell: (info) => {
                const running = info.getValue();
                return (
                    <span>
                        {running === "true"
                            ? t("ViewControlProgram_Status_Active")
                            : t("ViewControlProgram_Status_Inactive")}
                    </span>
                );
            },
            enableColumnFilter: true,
            enableGlobalFilter: false,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode<ProgramTableRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(row, ID.STATUS, value)
            ),
            size: TST_COLUMN_SIZE.XS,
        }),
        columnHelper.accessor(
            (row) => formatTagsAndCategoriesString(row.tagIds, tagsData),
            {
                id: ID.TAGS,
                header: () => t("ControlProgram_List_Header_Tags"),
                cell: (info) => {
                    const tagIds = info.row.original.tagIds;
                    const tags = tagsData.tags.filter((tag) =>
                        tagIds.includes(tag.id)
                    );
                    const tagCategories = tagsData.tagCategories;
                    return (
                        <TagList
                            mode="HOVER"
                            tags={tags}
                            tagCategories={tagCategories}
                        />
                    );
                },
                enableColumnFilter: true,
                sortingFn: (rowA, rowB) =>
                    tagsTSTSortingFn(
                        rowA.original.tagIds,
                        rowB.original.tagIds
                    ),
                filterFn: createFilterFnWithMode<ProgramTableRow, string[]>(
                    (row, value) => tagsTSTFilterFn(row.original.tagIds, value)
                ),
            }
        ),
    ];
}

function getFilterComponent(
    column: Column<ProgramTableRow>,
    target: FilterTarget,
    tagsData: TagsData,
    t: TFunction
) {
    const getFilterHelpers =
        createFilterHelpersGetter<EntityProgramListColumnFilters>(column);
    const columnId = column.id as EntityProgramListColumnId;

    switch (columnId) {
        case ID.NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((title) => ({
                label: title,
                value: title,
                customLabel: <CustomEllipsisLabel text={title} />,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Filter_ProgramName_Placeholder")}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.CONTROL_COUNT: {
            throw new Error("Control Count should be marked as not filterable");
        }
        case ID.STATUS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((action) => ({
                label: mapStatusToLabel(action, t),
                value: action,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Filter_Status_Placeholder")}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.TAGS: {
            const rows = column.getFacetedRowModel();
            const tagIdsPerRow = rows.flatRows.map(
                (row) => row.original.tagIds
            );
            const uniqueTagIds = Array.from(new Set(tagIdsPerRow.flat()));
            const { tags, tagCategories } = tagsData;
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={tagCategories}
                    tags={tags}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t("Filter_Tags_Placeholder")}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        default:
            assertNever(columnId);
    }
}

function formatToTagsData(
    tags: ControlProgramTagView[],
    tagCategories: ControlProgramTagCategoryView[]
): TagsData {
    const tagsData: TagsData = {
        tags: tags.map((t) => ({
            id: t.id,
            name: t.name,
            tagCategoryId: t.controlProgramTagCategoryId,
        })),
        tagCategories,
    };
    return tagsData;
}

function mapStatusToLabel(status: string, t: TFunction): string {
    switch (status) {
        case "true":
            return t("ViewControlProgram_Status_Active");
        case "false":
            return t("ViewControlProgram_Status_Inactive");
        default:
            return status;
    }
}
